import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { supabase } from '@/integrations/supabase/client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Globe, Palette, Loader2, CheckCircle, AlertTriangle } from 'lucide-react';

interface SubdomainRequestFormProps {
  onSuccess?: () => void;
}

export function SubdomainRequestForm({ onSuccess }: SubdomainRequestFormProps) {
  const { currentWorkspace } = useWorkspace();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const workspaceId = currentWorkspace?.id;

  const [formData, setFormData] = useState({
    requested_subdomain: '',
    requested_domain: 'billportal.ai', // Default domain
    requested_brand_name: '',
    requested_primary_color: '#3b82f6',
    requested_secondary_color: '#10b981',
    requested_accent_color: '#8b5cf6',
    requested_logo_url: '',
    requested_favicon_url: '',
    additional_notes: '',
  });

  const createRequestMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      if (!workspaceId) throw new Error('No workspace selected');
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const fullRequestedDomain = `${data.requested_subdomain}.${data.requested_domain}`;

      const { data: request, error } = await supabase
        .from('subdomain_requests')
        .insert({
          workspace_id: workspaceId,
          requested_subdomain: data.requested_subdomain,
          requested_domain: data.requested_domain,
          full_requested_domain: fullRequestedDomain,
          requested_brand_name: data.requested_brand_name,
          requested_primary_color: data.requested_primary_color,
          requested_secondary_color: data.requested_secondary_color,
          requested_accent_color: data.requested_accent_color,
          requested_logo_url: data.requested_logo_url || null,
          requested_favicon_url: data.requested_favicon_url || null,
          additional_notes: data.additional_notes || null,
          requested_by: user.id,
          status: 'pending',
        })
        .select()
        .single();

      if (error) throw error;
      return request;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subdomain-requests', workspaceId] });
      toast({
        title: 'Request submitted',
        description: 'Your subdomain request has been submitted and is pending admin approval.',
      });
      
      // Reset form
      setFormData({
        requested_subdomain: '',
        requested_domain: 'billportal.ai',
        requested_brand_name: '',
        requested_primary_color: '#3b82f6',
        requested_secondary_color: '#10b981',
        requested_accent_color: '#8b5cf6',
        requested_logo_url: '',
        requested_favicon_url: '',
        additional_notes: '',
      });
      
      onSuccess?.();
    },
    onError: (error: Error) => {
      toast({
        title: 'Error submitting request',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.requested_subdomain.trim()) {
      toast({
        title: 'Subdomain required',
        description: 'Please enter a subdomain name',
        variant: 'destructive',
      });
      return;
    }

    if (!formData.requested_brand_name.trim()) {
      toast({
        title: 'Brand name required',
        description: 'Please enter a brand name for your subdomain',
        variant: 'destructive',
      });
      return;
    }

    // Validate subdomain format
    const subdomainRegex = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/;
    if (!subdomainRegex.test(formData.requested_subdomain)) {
      toast({
        title: 'Invalid subdomain',
        description: 'Subdomain must be lowercase alphanumeric with hyphens, 3-63 characters',
        variant: 'destructive',
      });
      return;
    }

    createRequestMutation.mutate(formData);
  };

  const fullDomain = formData.requested_subdomain 
    ? `${formData.requested_subdomain}.${formData.requested_domain}`
    : '';

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Globe className="h-5 w-5" />
          Request Custom Subdomain
        </CardTitle>
        <CardDescription>
          Request a custom subdomain with your branding. Your request will be reviewed by workspace administrators.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Subdomain Input */}
          <div className="space-y-2">
            <Label htmlFor="subdomain">Subdomain</Label>
            <div className="flex items-center gap-2">
              <Input
                id="subdomain"
                value={formData.requested_subdomain}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  requested_subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '')
                }))}
                placeholder="yourcompany"
                className="flex-1"
                maxLength={63}
              />
              <span className="text-muted-foreground">.</span>
              <select
                value={formData.requested_domain}
                onChange={(e) => setFormData(prev => ({ ...prev, requested_domain: e.target.value }))}
                className="px-3 py-2 border rounded-md bg-background"
              >
                <option value="billportal.ai">billportal.ai</option>
                <option value="billportal.io">billportal.io</option>
              </select>
            </div>
            {fullDomain && (
              <p className="text-sm text-muted-foreground">
                Full domain: <span className="font-mono">{fullDomain}</span>
              </p>
            )}
            <p className="text-xs text-muted-foreground">
              Subdomain must be 3-63 characters, lowercase alphanumeric with hyphens only
            </p>
          </div>

          {/* Brand Name */}
          <div className="space-y-2">
            <Label htmlFor="brandName">Brand Name *</Label>
            <Input
              id="brandName"
              value={formData.requested_brand_name}
              onChange={(e) => setFormData(prev => ({ ...prev, requested_brand_name: e.target.value }))}
              placeholder="Your Company Name"
              required
            />
          </div>

          {/* Brand Colors */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="primaryColor">Primary Color</Label>
              <div className="flex gap-2">
                <Input
                  id="primaryColor"
                  type="color"
                  value={formData.requested_primary_color}
                  onChange={(e) => setFormData(prev => ({ ...prev, requested_primary_color: e.target.value }))}
                  className="w-16"
                />
                <Input
                  value={formData.requested_primary_color}
                  onChange={(e) => setFormData(prev => ({ ...prev, requested_primary_color: e.target.value }))}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="secondaryColor">Secondary Color</Label>
              <div className="flex gap-2">
                <Input
                  id="secondaryColor"
                  type="color"
                  value={formData.requested_secondary_color}
                  onChange={(e) => setFormData(prev => ({ ...prev, requested_secondary_color: e.target.value }))}
                  className="w-16"
                />
                <Input
                  value={formData.requested_secondary_color}
                  onChange={(e) => setFormData(prev => ({ ...prev, requested_secondary_color: e.target.value }))}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="accentColor">Accent Color</Label>
              <div className="flex gap-2">
                <Input
                  id="accentColor"
                  type="color"
                  value={formData.requested_accent_color}
                  onChange={(e) => setFormData(prev => ({ ...prev, requested_accent_color: e.target.value }))}
                  className="w-16"
                />
                <Input
                  value={formData.requested_accent_color}
                  onChange={(e) => setFormData(prev => ({ ...prev, requested_accent_color: e.target.value }))}
                />
              </div>
            </div>
          </div>

          {/* Logo and Favicon URLs */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="logoUrl">Logo URL (optional)</Label>
              <Input
                id="logoUrl"
                type="url"
                value={formData.requested_logo_url}
                onChange={(e) => setFormData(prev => ({ ...prev, requested_logo_url: e.target.value }))}
                placeholder="https://example.com/logo.png"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="faviconUrl">Favicon URL (optional)</Label>
              <Input
                id="faviconUrl"
                type="url"
                value={formData.requested_favicon_url}
                onChange={(e) => setFormData(prev => ({ ...prev, requested_favicon_url: e.target.value }))}
                placeholder="https://example.com/favicon.ico"
              />
            </div>
          </div>

          {/* Additional Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Additional Notes (optional)</Label>
            <Textarea
              id="notes"
              value={formData.additional_notes}
              onChange={(e) => setFormData(prev => ({ ...prev, additional_notes: e.target.value }))}
              placeholder="Any additional information or special requirements..."
              rows={4}
            />
          </div>

          {/* Preview */}
          {formData.requested_brand_name && (
            <div className="p-4 border rounded-lg bg-muted/50">
              <h4 className="font-semibold mb-2">Brand Preview</h4>
              <div 
                className="p-4 rounded-lg"
                style={{ 
                  backgroundColor: `${formData.requested_primary_color}10`,
                  borderColor: `${formData.requested_primary_color}30`,
                  borderWidth: '1px',
                  borderStyle: 'solid'
                }}
              >
                <div 
                  className="text-xl font-bold mb-2"
                  style={{ color: formData.requested_primary_color }}
                >
                  {formData.requested_brand_name}
                </div>
                <div className="flex gap-2">
                  <div 
                    className="h-12 flex-1 rounded"
                    style={{ backgroundColor: formData.requested_primary_color }}
                  />
                  <div 
                    className="h-12 flex-1 rounded"
                    style={{ backgroundColor: formData.requested_secondary_color }}
                  />
                  <div 
                    className="h-12 flex-1 rounded"
                    style={{ backgroundColor: formData.requested_accent_color }}
                  />
                </div>
              </div>
            </div>
          )}

          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Your request will be reviewed by workspace administrators. You'll be notified once it's approved or if additional information is needed.
            </AlertDescription>
          </Alert>

          <div className="flex gap-3">
            <Button 
              type="submit"
              disabled={createRequestMutation.isPending}
              className="flex-1"
            >
              {createRequestMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Submit Request
                </>
              )}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

